import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { toLocalInput } from "./AnnouncementManagementModal";
import { useModalMotion } from "../../components/motion/useModalMotion";
import { ModalShell } from "../../components/motion/ModalShell";

// Advertiser identity fields tracked through the approval / change-log flow.
const TRACKED_FIELDS = [
  ["name", "Name"],
  ["website", "Website"],
  ["category", "Category"],
  ["brand_context", "Brand context"],
  ["brand_suitability", "Brand suitability"],
];

const tabBtn = (active) => ({
  background: "none",
  border: "none",
  borderBottom: active ? "2px solid #F90091" : "2px solid transparent",
  color: active ? "#151515" : "#7b7b7b",
  fontWeight: 600,
  fontSize: 13,
  padding: "6px 2px",
  marginRight: 20,
  cursor: "pointer",
});

const rowStyle = {
  border: "1px solid #E2E2E2",
  borderRadius: 10,
  padding: "12px 14px",
  marginBottom: 10,
  background: "#FAFAFA",
};

const pill = (bg, fg) => ({
  background: bg,
  color: fg,
  borderRadius: 20,
  fontSize: 11,
  fontWeight: 600,
  padding: "2px 8px",
});

const hintStyle = { textAlign: "center", padding: 28, color: "#7b7b7b", fontSize: 13 };

const errorStyle = {
  padding: "8px 12px",
  background: "#FFF5F5",
  border: "1px solid #fca5a5",
  borderRadius: 8,
  color: "#e53935",
  fontSize: 13,
  marginBottom: 14,
};

function statusPill(status) {
  if (status === "approved") return pill("#DCFCE7", "#166534");
  if (status === "rejected") return pill("#FEE2E2", "#991B1B");
  return pill("#F3F4F6", "#6B7280");
}

function fmt(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

function AdvertiserChangesList({ rows }) {
  if (rows.length === 0) return <div style={hintStyle}>No advertiser settings changes yet.</div>;
  return rows.map((row) => {
    const prev = row.previous_values || {};
    const cleared = row.cleared_fields || [];
    // A cleared field has row[k] === null, which the "did it change" check
    // below would otherwise treat as "not part of this proposal" and hide —
    // losing the fact that the proposer explicitly wiped an existing value.
    // Only surface it when there was something to clear (prev[k] != null);
    // a field that was already null and is still null is not a change.
    const diffs = TRACKED_FIELDS.filter(([k]) =>
      cleared.includes(k) ? prev[k] != null : row[k] != null && row[k] !== prev[k]
    );
    return (
      <div key={row.id} style={rowStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={statusPill(row.status)}>{row.status}</span>
          <span style={{ fontSize: 12, color: "#7b7b7b" }}>
            {toLocalInput(row.reviewed_at || row.updated_at).replace("T", " ")}
          </span>
        </div>
        <div style={{ fontSize: 12, color: "#3E3E3E", marginBottom: 8 }}>
          Proposed by <strong>{fmt(row.proposed_by_email)}</strong>
          {row.reviewed_by ? <> · reviewed by <strong>{row.reviewed_by}</strong></> : null}
        </div>
        {diffs.length === 0 ? (
          <div style={{ fontSize: 12, color: "#7b7b7b" }}>No field differences recorded.</div>
        ) : (
          diffs.map(([k, label]) => (
            <div key={k} style={{ fontSize: 12, color: "#151515", marginTop: 3, overflowWrap: "anywhere" }}>
              <strong>{label}:</strong> {fmt(prev[k])} → {fmt(row[k])}
            </div>
          ))
        )}
      </div>
    );
  });
}

function CampaignChangesList({ rows }) {
  if (rows.length === 0) return <div style={hintStyle}>No campaign settings changes yet.</div>;
  return rows.map((row) => (
    <div key={row.id} style={rowStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{row.month_year}</span>
        <span style={statusPill(row.status)}>{row.status}</span>
      </div>
      <div style={{ fontSize: 12, color: "#3E3E3E", marginBottom: 6 }}>
        {toLocalInput(row.updated_at).replace("T", " ")}
        {row.submitted_by ? <> · submitted by <strong>{row.submitted_by}</strong></> : null}
        {row.reviewed_by ? <> · reviewed by <strong>{row.reviewed_by}</strong></> : null}
      </div>
      {row.review_notes ? (
        <div style={{ fontSize: 12, color: "#7b7b7b", marginBottom: 6, fontStyle: "italic" }}>{row.review_notes}</div>
      ) : null}
      <div style={{ fontSize: 12, color: "#151515", display: "flex", flexWrap: "wrap", gap: "2px 14px" }}>
        <span><strong>Spend cap:</strong> {fmt(row.spend_cap)}</span>
        <span><strong>Fixed CPA:</strong> {fmt(row.fixed_cpa)}</span>
        {row.pricing_mode ? <span><strong>Pricing:</strong> {row.pricing_mode}</span> : null}
        <span><strong>CPA types:</strong> {(row.cpa_types || []).length}</span>
        <span><strong>Audio types:</strong> {(row.audio_types || []).length}</span>
        <span><strong>Geo:</strong> {(row.geo || []).length}</span>
      </div>
    </div>
  ));
}

export function AdvertiserChangeHistoryModal({ advertiserId, advertiserName, onClose }) {
  const motion = useModalMotion(onClose, { closeOnBackdrop: true });
  const { requestClose } = motion;
  const [view, setView] = useState("advertiser");
  const [advertiserRows, setAdvertiserRows] = useState([]);
  const [campaignRows, setCampaignRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!supabase || advertiserId == null) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const [advRes, csRes] = await Promise.all([
      supabase
        .from("advertiser_settings_changes")
        .select("*")
        .eq("advertiser_id", advertiserId)
        .neq("status", "pending")
        // Postgres defaults to NULLS FIRST for DESC — without nullsFirst:
        // false, any non-pending row without reviewed_at would sort to the
        // top of "most recent". Matches migration 074's own history index,
        // which orders on coalesce(reviewed_at, updated_at) desc.
        .order("reviewed_at", { ascending: false, nullsFirst: false }),
      supabase
        .from("campaign_settings")
        .select("*")
        .eq("advertiser_id", advertiserId)
        .order("updated_at", { ascending: false }),
    ]);
    if (advRes.error || csRes.error) {
      setError((advRes.error || csRes.error).message || "Failed to load change history.");
    } else {
      setAdvertiserRows(advRes.data ?? []);
      setCampaignRows(csRes.data ?? []);
    }
    setLoading(false);
  }, [advertiserId]);

  useEffect(() => { load(); }, [load]);

  return (
    <ModalShell motion={motion} labelledBy="advertiser-change-history-title" maxWidth={640} panelStyle={{ padding: 28 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h4 id="advertiser-change-history-title" style={{ margin: 0, fontSize: 16 }}>Change history</h4>
          <button onClick={requestClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#7b7b7b" }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: "#7b7b7b", marginBottom: 14 }}>{advertiserName || `Advertiser #${advertiserId}`}</div>

        <div style={{ borderBottom: "1px solid #E2E2E2", marginBottom: 16 }}>
          <button style={tabBtn(view === "advertiser")} onClick={() => setView("advertiser")}>Advertiser settings</button>
          <button style={tabBtn(view === "campaign")} onClick={() => setView("campaign")}>Campaign settings</button>
        </div>

        {error && <div style={errorStyle}>{error}</div>}
        {loading ? (
          <div style={hintStyle}>Loading…</div>
        ) : view === "advertiser" ? (
          <AdvertiserChangesList rows={advertiserRows} />
        ) : (
          <CampaignChangesList rows={campaignRows} />
        )}
    </ModalShell>
  );
}
